import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  useTheme,
  Grid2,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  TextField,
  Collapse,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  Close as CloseIcon,
  SwapHoriz as SwapIcon,
  ArrowForward as ArrowForwardIcon,
  AddBoxOutlined as AddBoxOutlinedIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { BoxTransferStockLine } from "../../../../../../models/SwageazyInventory/SwageazyInventory";

interface SwageazyInventoryBoxTransferPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  errorColor: string;
}> = ({ label, required, errorColor }) => (
  <CustomTypography
    variant="heading6"
    style={{ fontWeight: 600, marginBottom: 10 }}
  >
    {label}
    {required ? (
      <span style={{ color: errorColor, marginLeft: 4 }}>*</span>
    ) : null}
  </CustomTypography>
);

const INITIAL_GENERATE_BOX_STATE = {
  showPanel: false,
  rackId: undefined as string | undefined,
  isGenerating: false,
  error: null as string | null,
};

const SwageazyInventoryBoxTransferPopup: React.FC<
  SwageazyInventoryBoxTransferPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { swageazyInventoryStore, warehouseStore } = useStores();
  const { showError } = useNotification();

  const form = swageazyInventoryStore.boxTransferFormData ?? {};
  const warehouseBoxes = swageazyInventoryStore.warehouseBoxes ?? [];
  const isPrefillLocked = Boolean(
    form.prefill_sku && form.prefill_product_id,
  );

  const [generateBoxState, setGenerateBoxState] = useState(
    INITIAL_GENERATE_BOX_STATE,
  );
  const updateGenerateBoxState = (
    patch: Partial<typeof INITIAL_GENERATE_BOX_STATE>,
  ) => {
    setGenerateBoxState((prev) => ({ ...prev, ...patch }));
  };

  const boxOptions = warehouseBoxes.map((box) => ({
    id: String(box.id),
    value: box.current_rack
      ? `${box.id} (${box.current_rack})`
      : String(box.id),
  }));

  const sourceBoxOptions = boxOptions;
  const destBoxOptions = boxOptions.filter(
    (box) =>
      box.id.toUpperCase() !== String(form.source_box_id || "").toUpperCase(),
  );

  const stockLines = form.stock_lines || [];
  const selectedLines = stockLines.filter((line) => line.selected);
  const selectedCount = selectedLines.length;
  const selectedQty = selectedLines.reduce(
    (sum, line) => sum + Number(line.transfer_quantity || 0),
    0,
  );
  const hasInvalidTransferQty = selectedLines.some((line) => {
    const qty = Number(line.transfer_quantity);
    const available = Number(line.quantity);
    return (
      !Number.isFinite(qty) ||
      qty <= 0 ||
      !Number.isFinite(available) ||
      qty > available
    );
  });
  const allSelected =
    stockLines.length > 0 && selectedCount === stockLines.length;
  const someSelected = selectedCount > 0 && !allSelected;

  useEffect(() => {
    swageazyInventoryStore.ensureBoxTransferState?.();

    if (!open) {
      swageazyInventoryStore.resetBoxTransferFormData?.();
      setGenerateBoxState(INITIAL_GENERATE_BOX_STATE);
      warehouseStore.clearWarehouseRacks();
    } else {
      warehouseStore.searchWarehouse().catch(() => {
        showError("Error", "Failed to load warehouses.");
      });
      if (form.warehouse_id) {
        handleLoadWarehouseRacks(form.warehouse_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !isPrefillLocked || !form.warehouse_id) return;
    const match = warehouseStore.warehouses?.find(
      (item) => String(item?.id) === String(form.warehouse_id),
    );
    if (!match) return;
    const label = String(match.identifier || match.id);
    if (form.warehouse_name === label) return;
    swageazyInventoryStore.setBoxTransferFormData({
      warehouse_name: label,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPrefillLocked, form.warehouse_id, warehouseStore.warehouses]);

  const handleLoadWarehouseRacks = (warehouseId?: string) => {
    if (!warehouseId) {
      warehouseStore.clearWarehouseRacks();
      updateGenerateBoxState({ rackId: undefined });
      return;
    }

    warehouseStore
      .searchWarehouseRack(String(warehouseId), "")
      .then(() => {
        if (warehouseStore.warehouseRacks?.length === 1) {
          updateGenerateBoxState({
            rackId: String(warehouseStore.warehouseRacks[0]?.id || ""),
          });
        } else {
          updateGenerateBoxState({ rackId: undefined });
        }
      })
      .catch((err: any) => {
        updateGenerateBoxState({ rackId: undefined });
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load racks.",
        );
      });
  };

  const handleLoadWarehouseBoxes = (warehouseId?: string) => {
    swageazyInventoryStore
      .loadWarehouseBoxes(warehouseId || "")
      .catch((err: any) => {
        if (!warehouseId) return;
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load boxes.",
        );
      });
  };

  const handleGenerateDestinationBox = async () => {
    updateGenerateBoxState({ error: null });

    if (!form.warehouse_id) {
      updateGenerateBoxState({ error: "Select a warehouse first." });
      return;
    }
    if (!generateBoxState.rackId?.trim()) {
      updateGenerateBoxState({ error: "Select a rack to generate a box." });
      return;
    }

    try {
      updateGenerateBoxState({ isGenerating: true });
      const boxId = await swageazyInventoryStore.generateBoxId(
        generateBoxState.rackId.trim(),
        String(form.warehouse_id),
      );
      if (!boxId) {
        throw new Error("Failed to generate box ID. Please try again.");
      }

      await swageazyInventoryStore.loadWarehouseBoxes(String(form.warehouse_id));
      swageazyInventoryStore.setBoxTransferFormData({
        dest_box_id: boxId,
      });
      updateGenerateBoxState({
        showPanel: false,
        isGenerating: false,
        error: null,
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate box. Please try again.";
      updateGenerateBoxState({
        error: message,
        isGenerating: false,
      });
    }
  };

  const handleLoadStock = () => {
    if (isPrefillLocked) return;

    if (!form.warehouse_id) {
      showError("Validation Error", "Please select a warehouse.");
      return;
    }
    if (!form.source_box_id?.trim()) {
      showError("Validation Error", "Please select a source box.");
      return;
    }

    swageazyInventoryStore
      .loadBoxTransferStock(form.source_box_id.trim())
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load source box stock.",
        );
      });
  };

  const updateStockLine = (
    sku: string,
    productId: number,
    patch: Partial<BoxTransferStockLine>,
  ) => {
    // Prefill mode: SKU stays selected; only qty can change.
    const safePatch =
      isPrefillLocked && patch.selected === false
        ? { ...patch, selected: true }
        : patch;

    const lines = stockLines.map((line) => {
      if (line.sku === sku && line.product_id === productId) {
        return { ...line, ...safePatch };
      }
      return line;
    });
    swageazyInventoryStore.setBoxTransferFormData({ stock_lines: lines });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (isPrefillLocked) return;
    const lines = stockLines.map((line) => ({
      ...line,
      selected: checked,
      transfer_quantity: line.transfer_quantity || line.quantity,
    }));
    swageazyInventoryStore.setBoxTransferFormData({ stock_lines: lines });
  };

  const canSubmit =
    Boolean(form.warehouse_id) &&
    Boolean(form.source_box_id?.trim()) &&
    Boolean(form.dest_box_id?.trim()) &&
    form.source_box_id?.trim().toUpperCase() !==
      form.dest_box_id?.trim().toUpperCase() &&
    selectedCount > 0 &&
    selectedQty > 0 &&
    !hasInvalidTransferQty &&
    !generateBoxState.isGenerating &&
    !swageazyInventoryStore.isBoxTransferLoading &&
    !swageazyInventoryStore.isLoading;

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    p: 2,
    backgroundColor: theme.palette.customColors?.grayBackGroundColor || "#fafafa",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Above stock-quantity dialog (zIndex 2000) when opened from variants.
      sx={{ zIndex: 2200 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "92vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SwapIcon color="primary" />
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Box Transfer
            </CustomTypography>
            <CustomTypography
              variant="body3"
              sx={{
                color: theme.palette.text.secondary,
                display: "block",
                mt: 0.25,
              }}
            >
              Move stock between boxes in the same warehouse
            </CustomTypography>
            {form.prefill_sku ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${form.prefill_sku}${
                  form.source_box_id ? ` @ ${form.source_box_id}` : ""
                }`}
                sx={{ mt: 1 }}
              />
            ) : null}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Grid2 container spacing={2} sx={{ mb: 2 }}>
          <Grid2 size={{ xs: 12 }}>
            <FieldLabel
              label="Warehouse"
              required
              errorColor={theme.palette.error.main}
            />
            <AppSearchDropdownMulti
              title="Select Warehouse"
              local
              isMultiSelect={false}
              disabled={isPrefillLocked}
              backgroundColor={theme.palette.customColors.grayBackGroundColor}
              borderColor={theme.palette.customColors.greyBodyBackground}
              minWidth="100%"
              list={
                warehouseStore.warehouses?.map((item) => ({
                  id: String(item?.id),
                  value: String(item?.identifier || item?.id),
                })) || []
              }
              value={
                form.warehouse_id
                  ? [
                      {
                        id: String(form.warehouse_id),
                        value: String(
                          form.warehouse_name || form.warehouse_id,
                        ),
                      },
                    ]
                  : []
              }
              handleSelect={(selectedItem: FilterValue[]) => {
                if (isPrefillLocked) return;
                if (selectedItem?.length > 0) {
                  swageazyInventoryStore.setBoxTransferFormData({
                    warehouse_id: selectedItem[0].id,
                    warehouse_name: selectedItem[0].value,
                    source_box_id: undefined,
                    dest_box_id: undefined,
                    stock_lines: [],
                    prefill_sku: undefined,
                    prefill_product_id: undefined,
                  });
                  setGenerateBoxState(INITIAL_GENERATE_BOX_STATE);
                  handleLoadWarehouseBoxes(selectedItem[0].id);
                  handleLoadWarehouseRacks(selectedItem[0].id);
                } else {
                  swageazyInventoryStore.setBoxTransferFormData({
                    warehouse_id: undefined,
                    warehouse_name: undefined,
                    source_box_id: undefined,
                    dest_box_id: undefined,
                    stock_lines: [],
                    prefill_sku: undefined,
                    prefill_product_id: undefined,
                  });
                  setGenerateBoxState(INITIAL_GENERATE_BOX_STATE);
                  handleLoadWarehouseBoxes(undefined);
                  handleLoadWarehouseRacks(undefined);
                }
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} alignItems="stretch">
          <Grid2 size={{ xs: 12, md: 5.5 }}>
            <Box sx={sectionSx}>
              <CustomTypography
                variant="heading6"
                fontWeight={700}
                sx={{ mb: 1.5, display: "block" }}
              >
                From
              </CustomTypography>
              <FieldLabel
                label="Source Box"
                required
                errorColor={theme.palette.error.main}
              />
              <AppSearchDropdownMulti
                title="Select Source Box"
                local
                isMultiSelect={false}
                disabled={isPrefillLocked}
                backgroundColor={theme.palette.common.white}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="100%"
                list={sourceBoxOptions}
                value={
                  form.source_box_id
                    ? [
                        {
                          id: form.source_box_id,
                          value:
                            sourceBoxOptions.find(
                              (box) => box.id === form.source_box_id,
                            )?.value || form.source_box_id,
                        },
                      ]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (isPrefillLocked) return;
                  if (selectedItem?.length > 0) {
                    const selectedBoxId = selectedItem[0].id;
                    swageazyInventoryStore.setBoxTransferFormData({
                      source_box_id: selectedBoxId,
                      stock_lines: [],
                      dest_box_id:
                        form.dest_box_id === selectedBoxId
                          ? undefined
                          : form.dest_box_id,
                      prefill_sku: undefined,
                      prefill_product_id: undefined,
                    });
                    swageazyInventoryStore
                      .loadBoxTransferStock(selectedBoxId)
                      .catch((err: any) => {
                        showError(
                          "Error",
                          err?.response?.data?.message ||
                            err?.message ||
                            "Failed to load source box stock.",
                        );
                      });
                  } else {
                    swageazyInventoryStore.setBoxTransferFormData({
                      source_box_id: undefined,
                      stock_lines: [],
                      prefill_sku: undefined,
                      prefill_product_id: undefined,
                    });
                  }
                }}
              />
            </Box>
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 1 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowForwardIcon
              sx={{
                color: theme.palette.text.secondary,
                transform: { xs: "rotate(90deg)", md: "none" },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5.5 }}>
            <Box sx={sectionSx}>
              <CustomTypography
                variant="heading6"
                fontWeight={700}
                sx={{ mb: 1.5, display: "block" }}
              >
                To
              </CustomTypography>
              <FieldLabel
                label="Destination Box"
                required
                errorColor={theme.palette.error.main}
              />
              <AppSearchDropdownMulti
                title="Select Destination Box"
                local
                isMultiSelect={false}
                backgroundColor={theme.palette.common.white}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="100%"
                list={destBoxOptions}
                value={
                  form.dest_box_id
                    ? [
                        {
                          id: form.dest_box_id,
                          value:
                            destBoxOptions.find(
                              (box) => box.id === form.dest_box_id,
                            )?.value || form.dest_box_id,
                        },
                      ]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem?.length > 0) {
                    swageazyInventoryStore.setBoxTransferFormData({
                      dest_box_id: selectedItem[0].id,
                    });
                    updateGenerateBoxState({ error: null });
                  } else {
                    swageazyInventoryStore.setBoxTransferFormData({
                      dest_box_id: undefined,
                    });
                  }
                }}
              />
              {form.dest_box_id ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`Selected: ${form.dest_box_id}`}
                  sx={{ mt: 1 }}
                />
              ) : null}

              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Box
                  onClick={() => {
                    if (!form.warehouse_id) {
                      updateGenerateBoxState({
                        showPanel: true,
                        error: "Select a warehouse first.",
                      });
                      return;
                    }
                    const nextShowPanel = !generateBoxState.showPanel;
                    updateGenerateBoxState({
                      showPanel: nextShowPanel,
                      error: null,
                    });
                    if (nextShowPanel && form.warehouse_id) {
                      handleLoadWarehouseRacks(form.warehouse_id);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AddBoxOutlinedIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <CustomTypography
                      variant="heading7"
                      fontWeight={600}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Generate new box
                    </CustomTypography>
                  </Box>
                  {generateBoxState.showPanel ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </Box>

                <Collapse in={generateBoxState.showPanel}>
                  <Box sx={{ mt: 1.5 }}>
                    <CustomTypography
                      variant="body3"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "block",
                        mb: 1.25,
                      }}
                    >
                      Create an empty box on a rack, then transfer stock into
                      it.
                    </CustomTypography>

                    <FieldLabel
                      label="Rack"
                      required
                      errorColor={theme.palette.error.main}
                    />
                    <AppSearchDropdownMulti
                      title="Select Rack"
                      local
                      isMultiSelect={false}
                      disabled={
                        !form.warehouse_id || generateBoxState.isGenerating
                      }
                      backgroundColor={theme.palette.common.white}
                      borderColor={
                        theme.palette.customColors.greyBodyBackground
                      }
                      minWidth="100%"
                      list={
                        warehouseStore.warehouseRacks?.map((item) => ({
                          id: String(item?.id),
                          value: String(item?.id),
                        })) || []
                      }
                      value={
                        generateBoxState.rackId
                          ? [
                              {
                                id: generateBoxState.rackId,
                                value: generateBoxState.rackId,
                              },
                            ]
                          : []
                      }
                      handleSelect={(selectedItem: FilterValue[]) => {
                        if (selectedItem?.length > 0) {
                          updateGenerateBoxState({
                            rackId: selectedItem[0].id,
                            error: null,
                          });
                        } else {
                          updateGenerateBoxState({ rackId: undefined });
                        }
                      }}
                    />

                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <AppButton
                        variant="primary"
                        label={
                          generateBoxState.isGenerating
                            ? "Generating..."
                            : "Generate Box"
                        }
                        onClick={() => {
                          void handleGenerateDestinationBox();
                        }}
                        disabled={
                          generateBoxState.isGenerating ||
                          !form.warehouse_id ||
                          !generateBoxState.rackId
                        }
                        customStyles={{ minWidth: 140 }}
                      />
                    </Box>

                    {generateBoxState.error ? (
                      <CustomTypography
                        variant="body3"
                        style={{
                          color: theme.palette.error.main,
                          marginTop: 8,
                          display: "block",
                        }}
                      >
                        {generateBoxState.error}
                      </CustomTypography>
                    ) : null}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Grid2>
        </Grid2>

        <Box sx={{ mt: 2.5 }}>
          <FieldLabel label="Reason" errorColor={theme.palette.error.main} />
          <CustomTextField
            width="100%"
            placeholder="e.g. Reorganize shelf"
            value={form.reason || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              swageazyInventoryStore.setBoxTransferFormData({
                reason: e.target.value,
              });
            }}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CustomTypography variant="heading6" fontWeight={700}>
                Items to Transfer
              </CustomTypography>
              {selectedCount > 0 ? (
                <Chip
                  size="small"
                  label={`${selectedCount} SKU · ${selectedQty} qty`}
                  color="primary"
                  variant="outlined"
                />
              ) : null}
            </Box>
            {!isPrefillLocked ? (
              <AppButton
                variant="grayscale"
                label="Reload Stock"
                onClick={handleLoadStock}
                customStyles={{ minWidth: 120 }}
                disabled={
                  swageazyInventoryStore.isBoxTransferLoading ||
                  !form.source_box_id
                }
              />
            ) : null}
          </Box>

          {swageazyInventoryStore.isBoxTransferLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : stockLines.length === 0 ? (
            <Box
              sx={{
                ...sectionSx,
                py: 4,
                textAlign: "center",
              }}
            >
              <CustomTypography
                variant="body3"
                sx={{ color: theme.palette.text.secondary }}
              >
                {form.source_box_id
                  ? "No stock found in the selected source box."
                  : "Select a source box to see available SKUs."}
              </CustomTypography>
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.customColors?.grayBackGroundColor ||
                        "#fafafa",
                    }}
                  >
                    {!isPrefillLocked ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <CustomTypography variant="body3" fontWeight={700}>
                        Product
                      </CustomTypography>
                    </TableCell>
                    <TableCell>
                      <CustomTypography variant="body3" fontWeight={700}>
                        SKU
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="right">
                      <CustomTypography variant="body3" fontWeight={700}>
                        Available
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 88 }}>
                      <CustomTypography variant="body3" fontWeight={700}>
                        Transfer Qty
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockLines.map((line) => (
                    <TableRow key={`${line.product_id}-${line.sku}`} hover>
                      {!isPrefillLocked ? (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={Boolean(line.selected)}
                            onChange={(e) =>
                              updateStockLine(line.sku, line.product_id, {
                                selected: e.target.checked,
                                transfer_quantity:
                                  line.transfer_quantity || line.quantity,
                              })
                            }
                          />
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <CustomTypography variant="body3" fontWeight={600}>
                          {line.product_name}
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: "block",
                          }}
                        >
                          #{line.product_id}
                        </CustomTypography>
                      </TableCell>
                      <TableCell>
                        <CustomTypography variant="body3">
                          {line.sku}
                        </CustomTypography>
                      </TableCell>
                      <TableCell align="right">
                        <CustomTypography variant="body3">
                          {line.quantity}
                        </CustomTypography>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          disabled={!isPrefillLocked && !line.selected}
                          value={
                            line.transfer_quantity === undefined ||
                            line.transfer_quantity === null
                              ? ""
                              : String(line.transfer_quantity)
                          }
                          error={
                            Boolean(line.selected) &&
                            Number(line.transfer_quantity) >
                              Number(line.quantity)
                          }
                          helperText={
                            Boolean(line.selected) &&
                            Number(line.transfer_quantity) >
                              Number(line.quantity)
                              ? `Max ${line.quantity}`
                              : undefined
                          }
                          inputProps={{
                            min: 1,
                            max: Number(line.quantity) || 0,
                            step: 1,
                            style: {
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              textAlign: "right",
                              paddingTop: 6,
                              paddingBottom: 6,
                              paddingRight: 8,
                            },
                          }}
                          sx={{
                            width: 64,
                            "& .MuiOutlinedInput-root": {
                              height: 28,
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: "0.65rem",
                              mx: 0,
                            },
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const available = Math.max(
                              0,
                              Number(line.quantity) || 0,
                            );
                            const raw = e.target.value;
                            if (raw === "") {
                              updateStockLine(line.sku, line.product_id, {
                                selected: true,
                                transfer_quantity: 0,
                              });
                              return;
                            }
                            const parsed = Number.parseInt(raw, 10);
                            if (Number.isNaN(parsed)) return;
                            const qty = Math.min(
                              available,
                              Math.max(0, parsed),
                            );
                            updateStockLine(line.sku, line.product_id, {
                              selected: true,
                              transfer_quantity: qty,
                            });
                          }}
                          onBlur={() => {
                            const available = Math.max(
                              0,
                              Number(line.quantity) || 0,
                            );
                            const qty = Number(line.transfer_quantity) || 0;
                            if (qty > available) {
                              updateStockLine(line.sku, line.product_id, {
                                selected: true,
                                transfer_quantity: available,
                              });
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography
          variant="body3"
          sx={{ color: theme.palette.text.secondary }}
        >
          {form.source_box_id && form.dest_box_id
            ? `${form.source_box_id} → ${form.dest_box_id}`
            : "Select source and destination boxes to continue"}
        </CustomTypography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <AppButton variant="grayscale" label="Cancel" onClick={onClose} />
          <AppButton
            variant="primary"
            label={
              selectedCount > 0
                ? `Transfer ${selectedQty} qty`
                : "Transfer"
            }
            onClick={onSave}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default observer(SwageazyInventoryBoxTransferPopup);
